import { FC, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { cloneDeep } from "lodash";
import { BeefOutputCalves } from "../../../../models/Baseline/BeefBaseline";
import BeefOutputInputField from "./BeefOutputInputField";
import { FormType } from "../../common";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { unitLong } from "../../../../utils/unit-utils";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { BeefIntervention } from "../../../../models/Intervention/BeefIntervention";

interface BeefOutputCalvesFormProps {
  fieldItemPrefix: string;
  outputBaselineData: BeefOutputCalves;
  formType: FormType;
  isOutputEdit: boolean;
  setIsOutputEdit: (isEditing: boolean) => void;
}

const BeefOutputCalvesForm: FC<BeefOutputCalvesFormProps> = ({
  isOutputEdit = false,
  fieldItemPrefix,
  formType,
  outputBaselineData,
  setIsOutputEdit,
}) => {
  const fc = useFormContext<BeefIntervention>();
  const userProfile = UserProfilePrefs.getInstance();
  const userUOM = userProfile.getUserUnitPrefs();
  const outputMassUnit = unitLong(userUOM.unitOutputMass as string);
  const currResetValue = useRef<BeefOutputCalves>();

  useEffect(() => {
    if (isOutputEdit) {
      currResetValue.current = cloneDeep(fc.getValues(fieldItemPrefix));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOutputEdit]);

  const resetEdit = () => {
    fc.setValue(
      `${fieldItemPrefix}.weanedCalvesToAnotherStage`,
      currResetValue.current?.weanedCalvesToAnotherStage ?? ""
    );
    fc.setValue(
      `${fieldItemPrefix}.averageWeightOfCalvesLeavingTheStage`,
      currResetValue.current?.averageWeightOfCalvesLeavingTheStage ?? ""
    );
    fc.setValue(
      `${fieldItemPrefix}.weanedCalvesSold`,
      currResetValue.current?.weanedCalvesSold ?? ""
    );

    fc.setValue(
      `${fieldItemPrefix}.numberOfMortalitiesCalves`,
      currResetValue.current?.numberOfMortalitiesCalves ?? ""
    );
    fc.setValue(
      `${fieldItemPrefix}.averageWeightAtMortalityCalves`,
      currResetValue.current?.averageWeightAtMortalityCalves ?? ""
    );

    setIsOutputEdit(false);
  };

  return (
    <>
      <BeefOutputInputField
        fieldItemPrefix={fieldItemPrefix}
        keyName="weanedCalvesToAnotherStage"
        originalKey="originalWeanedCalvesToAnotherStage"
        labelId="SUSTELL.STAGE.BEEF.OUTPUT.WEANED_CALVES_TO_ANOTHER_STAGE"
        editEnabled={isOutputEdit}
        outputData={outputBaselineData}
        adornmentId="SUSTELL.STAGE.BEEF.OUTPUT.WEANED_CALVES_TO_ANOTHER_STAGE.PLACEHOLDER"
        tooltipId="SUSTELL.STAGE.BEEF.OUTPUT.WEANED_CALVES_TO_ANOTHER_STAGE.TOOLTIP"
      />

      <BeefOutputInputField
        fieldItemPrefix={fieldItemPrefix}
        keyName="averageWeightOfCalvesLeavingTheStage"
        originalKey="originalAverageWeightOfCalvesLeavingTheStage"
        labelId="SUSTELL.STAGE.BEEF.OUTPUT.AVERAGE_WEIGHT_OF_CALVES_LEAVING_THE_STAGE"
        editEnabled={isOutputEdit}
        outputData={outputBaselineData}
        adornmentId="SUSTELL.STAGE.BEEF.OUTPUT.AVERAGE_WEIGHT_OF_CALVES_LEAVING_THE_STAGE.PLACEHOLDER"
        adornmentValues={{ unit: outputMassUnit }}
        tooltipId="SUSTELL.STAGE.BEEF.OUTPUT.AVERAGE_WEIGHT_OF_CALVES_LEAVING_THE_STAGE.TOOLTIP"
      />

      <BeefOutputInputField
        fieldItemPrefix={fieldItemPrefix}
        keyName="weanedCalvesSold"
        originalKey="originalWeanedCalvesSold"
        labelId="SUSTELL.STAGE.BEEF.OUTPUT.WEANED_CALVES_SOLD"
        editEnabled={isOutputEdit}
        outputData={outputBaselineData}
        adornmentId="SUSTELL.STAGE.BEEF.OUTPUT.WEANED_CALVES_SOLD.PLACEHOLDER"
        tooltipId="SUSTELL.STAGE.BEEF.OUTPUT.WEANED_CALVES_SOLD.TOOLTIP"
      />

      <BeefOutputInputField
        fieldItemPrefix={fieldItemPrefix}
        keyName="numberOfMortalitiesCalves"
        originalKey="originalNumberOfMortalitiesCalves"
        labelId="SUSTELL.STAGE.BEEF.OUTPUT.NUMBER_OF_MORTALITIES_CALVES"
        editEnabled={isOutputEdit}
        outputData={outputBaselineData}
        adornmentId="SUSTELL.STAGE.BEEF.OUTPUT.NUMBER_OF_MORTALITIES_CALVES.PLACEHOLDER"
        tooltipId="SUSTELL.STAGE.BEEF.OUTPUT.NUMBER_OF_MORTALITIES_CALVES.TOOLTIP"
      />

      <BeefOutputInputField
        fieldItemPrefix={fieldItemPrefix}
        keyName="averageWeightAtMortalityCalves"
        originalKey="originalAverageWeightAtMortalityCalves"
        labelId="SUSTELL.STAGE.BEEF.OUTPUT.AVERAGE_WEIGHT_AT_MORTALITY_CALVES"
        editEnabled={isOutputEdit}
        outputData={outputBaselineData}
        adornmentId="SUSTELL.STAGE.BEEF.OUTPUT.AVERAGE_WEIGHT_AT_MORTALITY_CALVES.PLACEHOLDER"
        adornmentValues={{ unit: outputMassUnit }}
        tooltipId="SUSTELL.STAGE.BEEF.OUTPUT.AVERAGE_WEIGHT_AT_MORTALITY_CALVES.TOOLTIP"
      />

      {formType !== FormType.View &&
        <EditModeButtons
          isEditing={isOutputEdit}
          onEdit={() => setIsOutputEdit(true)}
          onCancel={resetEdit}
        />}
    </>
  );
};

export default BeefOutputCalvesForm;
